<script setup lang="ts">
import VSpinner from '@stl/components/VSpinner.vue';
import { computed, ref, watch } from 'vue';
import VueSelect from 'vue-select';

export interface SelectOption {
  value: string | number | boolean | null;
  title: string;
  /** Shown in the menu but cannot be picked. */
  disabled?: boolean;
}

interface Props {
  options?: string[] | SelectOption[];
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  modelValue: string[] | string | number | boolean | null;
  multiple?: boolean;
  label?: string;
  noDrop?: boolean;
  taggable?: boolean;
  selectOnTab?: boolean;
  reduce?: Function;
  searchable?: boolean;
  clearable?: boolean;
  validator?: RegExp;
  validationMessage?: string;
  inputId?: string;
  selectable?: (option: any) => boolean;
}

const emit = defineEmits(['change', 'input', 'update:modelValue']);
const props = withDefaults(defineProps<Props>(), {
  options: undefined,
  disabled: false,
  isLoading: false,
  placeholder: '',
  multiple: false,
  label: 'title',
  noDrop: false,
  taggable: false,
  selectOnTab: true,
  searchable: false,
  clearable: false,
  validationMessage: 'Invalid input',
  validator: undefined,
  inputId: undefined,
  selectable: (option: any) => !(typeof option === 'object' && option?.disabled),
  reduce: (option: any) => {
    if (typeof option === 'object') {
      return (option as SelectOption).value;
    }
    return option;
  }
});

const showValidationMessage = ref(false);

const newValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
    emit('input', value);
    emit('change', value);
  }
});

watch(
  () => props.modelValue,
  (value) => {
    if (Array.isArray(value) && value.length > 0 && props.validator) {
      const isValid = props.validator?.test(value[value.length - 1]);
      if (!isValid) {
        value.pop();
        showValidationMessage.value = true;
        setTimeout(() => {
          showValidationMessage.value = false;
        }, 1500);
      }
    }
    emit('update:modelValue', value);
  }
);
</script>

<template lang="pug">
vue-select(v-model="newValue" :options="options" :disabled="disabled" :clearable="clearable" :multiple="multiple" :label="label" :no-drop="noDrop" :reduce="reduce" :taggable="taggable" :searchable="searchable" :placeholder="placeholder", :loading="isLoading" :select-on-tab="selectOnTab" :input-id="inputId" :selectable="selectable")
  template(#spinner="{ loading }")
    v-spinner(v-if="loading")
  template(v-if="showValidationMessage" #footer)
    .text-danger.mt-1 {{ validationMessage }}
</template>

<style lang="scss">
@import 'vue-select/dist/vue-select.css';
.v-select {
  position: relative;
  --vs-search-input-color: var(--bs-body-color);
  --vs-selected-color: var(--bs-body-color);
  --vs-border-width: var(--bs-border-width);
  --vs-border-style: solid;
  --vs-border-color: var(--bs-border-color);
  --vs-border-radius: var(--bs-border-radius);
  --vs-dropdown-color: var(--bs-body-color);
  --vs-dropdown-bg: var(--bs-body-bg);
  --vs-dropdown-option--active-color: var(--bs-body-color);
  --vs-dropdown-option--active-bg: var(--bs-tertiary-bg);
  --vs-controls-color: var(--bs-body-color);
  --vs-selected-bg: var(--bs-primary);
  --vs-disabled-bg: var(--bs-secondary-bg);

  .vs__dropdown-toggle {
    padding: 0.2rem 0.375rem 0.375rem 0.375rem;
  }
  &.vs--single {
    .vs__selected {
      margin-left: 0;
    }
    &.vs--open {
      .vs__selected {
        position: relative;
      }
    }
  }
  &.vs--open {
    .vs__dropdown-toggle {
      border-bottom-color: var(--vs-border-color);
      border-bottom-left-radius: var(--vs-border-radius);
      border-bottom-right-radius: var(--vs-border-radius);
    }
  }
}
.vs__dropdown-menu {
  border-top-style: solid;
  margin-top: 3px;
  border-radius: var(--vs-border-radius);
}
// vue-select paints disabled options a fixed light grey, which glares on dark themes.
.vs__dropdown-option--disabled {
  background: transparent;
  color: var(--bs-secondary-color);
  opacity: 0.6;
}
</style>
